<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="3">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-col>

      <v-col cols="3">
        <Search
          v-model="filters.subject"
          :label="$t('SUBJECT')"
          prepend-icon="search"
          :delay="500"
        />
      </v-col>

      <v-col cols="3">
        <Search
          v-model="filters.school"
          :label="$t('SCHOOL')"
          prepend-icon="search"
          :delay="500"
        />
      </v-col>

      <v-col cols="2">
        <Search
          v-model="filters.email_or_phone"
          :label="$t('fields.email_or_phone')"
          prepend-icon="search"
          :delay="500"
        />
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <v-spacer></v-spacer>

      <v-btn
        v-if="can('create teachers')"
        @click="showTeacherModal(0)"
        color="primary"
        dark
        class="mr-2"
      >
        {{ $t("NEW") }}
      </v-btn>

      <v-btn
        v-if="can('export teachers')"
        @click="exportData()"
        dark
      >
        {{ $t("EXPORT") }}
      </v-btn>
    </v-row>

    <v-data-table
      class="elevation-1"
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      :item-class="itemRowBackground"
    >
      <template v-slot:[`item.surname`]="{ item }">
        <router-link :to="{ name: 'teachers_show', params: { id: item.id } }">
          {{ item.surname }}
        </router-link>
      </template>

      <template v-slot:[`item.email`]="{ item }">
        {{ (item.addresses.length ? item.addresses[0].email : '') | lowercase }}
      </template>

      <template v-slot:[`item.alternative_email`]="{ item }">
        {{ (item.options.alternative_email || '') | lowercase }}
      </template>

      <template v-slot:[`item.phone`]="{ item }">
        {{ item.addresses.length ? item.addresses[0].phone : '' }}
      </template>

      <template v-slot:[`item.alternative_phone`]="{ item }">
        {{ item.options.alternative_phone || '' }}
      </template>

      <template v-slot:[`item.school`]="{ item }">
        {{ item.schools.length ? item.schools[0].school_name : '' }}
        <span v-if="item.schools.length">({{ item.schools[0].schools_types_name }})</span>
      </template>

      <template v-slot:[`item.city`]="{ item }">
        {{ item.addresses.length ? item.addresses[0].city_name : '' }}
      </template>

      <template v-slot:[`item.zone`]="{ item }">
        {{ item.addresses.length ? item.addresses[0].zone_name : '' }}
      </template>

      <template v-slot:[`item.address`]="{ item }">
        {{ item.addresses.length ? item.addresses[0].street : '' }}
      </template>

      <template v-slot:[`item.box`]="{ item }">
        <v-icon v-if="isInbox(item)">done</v-icon>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top v-if="can('edit teachers')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              :color="item.schools.length > 1 ? 'white' : 'blue'"
              v-bind="attrs"
              v-on="on"
              @click="showTeacherModal(item.id)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('EDIT') }}</span>
        </v-tooltip>
        <v-tooltip top v-if="can('delete teachers')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="red"
              v-bind="attrs"
              v-on="on"
              @click="deleteTeacher(item.id)"
            >
              <v-icon>cancel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('DELETE') }}</span>
        </v-tooltip>
        <v-tooltip right v-if="getHiddenNote(item)">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="info" v-bind="attrs" v-on="on">
              <v-icon>info</v-icon>
            </v-btn>
          </template>
          <span>{{ getHiddenNote(item) }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchTeachers"
    />
  </v-container>
</template>
<script>
  import { mapActions } from 'vuex';

  import { teachersService } from '../../utils/services/teachers.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import TeacherModal from '../../components/modals/Teachers/TeacherModal';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'TeacherList',
    components: { Search, Loader, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['surname'],
        },
        headers: [
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'left' },
          { text: this.$t('fields.surname'), value: 'surname' },
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.subject'), value: 'default_subject_name' },
          { text: this.$t('fields.school'), value: 'school', sortable: false },
          { text: this.$t('fields.email'), value: 'email', sortable: false },
          { text: this.$t('fields.alternative_email'), value: 'alternative_email', sortable: false },
          { text: this.$t('fields.phone'), value: 'phone', sortable: false },
          { text: this.$t('fields.alternative_phone'), value: 'alternative_phone', sortable: false },
          { text: this.$t('fields.city'), value: 'city', sortable: false },
          { text: this.$t('fields.address'), value: 'address', sortable: false },
          { text: this.$t('fields.zone'), value: 'zone', sortable: false },
          { text: this.$t('fields.box'), value: 'box', sortable: false },

        ],
        filters: {
          search: null,
          subject: null,
          school: null,
          email_or_phone: null,
        },
      };
    },
    computed: {

      requestFilters() {
        const filters = {
          search: this.filters.search,
          subject: this.filters.subject,
          school: this.filters.school,
          email_or_phone: this.filters.email_or_phone,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),

      itemRowBackground(item) {
        return item.schools.length > 1 ? 'blue' : null;
      },

      async fetchTeachers(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return teachersService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      exportData() {
        this.loading = true;
        return teachersService.exportData({
          filters: this.requestFilters,
        })
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'teachers.csv');
            document.body.appendChild(link);
            link.click();
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      isInbox(item) {
        return !item.schools.length;
      },

      getHiddenNote(item) {
        return item.options.notice_invisible || '';
      },

      showTeacherModal(id) {
        this.showModal({
          component: TeacherModal,
            data: {
              stored: {
                id,
                return_url: this.$route.path,
              },
          },
          options: {
            title: this.$t('TEACHER'),
            onSave: (
              form,
              error,
            ) => teachersService.save(form)
            .then((response) => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
              if (!id) {
                this.$router.push({ path: `/teachers/${response.data.teacher.id}/show?tab=school&returnUrl=${this.$route.path}` });
              }
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchTeachers(this.pagination.page);
            }),
          },
        });
      },
      async deleteTeacher(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return teachersService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchTeachers(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchTeachers(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchTeachers(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchTeachers(1);
          }
        },
      },
    },
  };
</script>
